import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  sourcingApi,
  type CandidateListResponse,
  type SearchProject,
  type SearchRun,
} from '@/api/sourcing';

const OUTREACH_STATUSES = [
  'found', 'reviewed', 'contacted', 'replied', 'interested', 'questionnaire',
  'case', 'interview', 'finalist', 'offer', 'rejected', 'no_response',
];

export default function SourcingProject() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<SearchProject | null>(null);
  const [runs, setRuns] = useState<SearchRun[]>([]);
  const [list, setList] = useState<CandidateListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scoring, setScoring] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const [grade, setGrade] = useState('');
  const [bucket, setBucket] = useState('');
  const [status, setStatus] = useState('');
  const [minScore, setMinScore] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const loadCandidates = useCallback(() => {
    if (!id) return;
    sourcingApi
      .listCandidates(id, {
        grade: grade || undefined,
        bucket: bucket || undefined,
        status: status || undefined,
        min_score: minScore || undefined,
        q: q || undefined,
        offset,
        limit,
      })
      .then(setList)
      .catch((e) => setError(e.message));
  }, [id, grade, bucket, status, minScore, q, offset]);

  const loadRuns = useCallback(() => {
    if (!id) return;
    sourcingApi.listRuns(id).then(setRuns).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    sourcingApi.getProject(id).then(setProject).catch((e) => setError(e.message));
    loadRuns();
  }, [id, loadRuns]);

  useEffect(loadCandidates, [loadCandidates]);

  // poll активных запусков раз в 15 сек
  useEffect(() => {
    const active = runs.filter((r) => r.status === 'queued' || r.status === 'running');
    if (!active.length) return;
    const timer = setInterval(async () => {
      for (const r of active) {
        try {
          await sourcingApi.getRun(r.id);
        } catch {
          /* ignore */
        }
      }
      loadRuns();
    }, 15000);
    return () => clearInterval(timer);
  }, [runs, loadRuns]);

  const importRun = async (runId: string) => {
    try {
      const res = await sourcingApi.importRun(runId);
      setNotice(t('sourcing.importedResult', { imported: res.imported, duplicates: res.duplicates }));
      loadRuns();
      loadCandidates();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onImportFile = async (file: File) => {
    if (!id) return;
    setError(null);
    try {
      const res = await sourcingApi.importFile(id, bucket || null, file);
      setNotice(t('sourcing.importedResult', { imported: res.imported, duplicates: res.duplicates }));
      loadRuns();
      loadCandidates();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const scoreAll = async () => {
    if (!id) return;
    setScoring(true);
    setNotice(t('sourcing.scoringInProgress'));
    try {
      const res = await sourcingApi.batchScore(id);
      setNotice(t('sourcing.scoredResult', { scored: res.scored, failed: res.failed }));
      loadCandidates();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setScoring(false);
    }
  };

  const bulkContacted = async () => {
    try {
      await sourcingApi.bulkContacted([...selected]);
      setSelected(new Set());
      loadCandidates();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const toggle = (cid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  return (
    <div>
      <button
        onClick={() => navigate('/sourcing')}
        className="text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer mb-3 flex items-center gap-1"
      >
        <i className="ri-arrow-left-line" /> {t('sourcing.back')}
      </button>

      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h1 className="text-xl font-heading font-bold text-foreground-950">
            {project?.name || t('sourcing.loading')}
          </h1>
          {list && (
            <p className="text-xs text-foreground-500 mt-0.5">
              {list.total} {t('sourcing.candidates')}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => navigate(`/sourcing/${id}/new-run`)}
            className="px-3 py-2 rounded-xl bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-play-line" /> {t('sourcing.newRun')}
          </button>
          <button
            onClick={() => fileInput.current?.click()}
            className="px-3 py-2 rounded-xl border border-background-200 text-xs font-medium text-foreground-700 hover:bg-background-100 cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-upload-2-line" /> {t('sourcing.importFile')}
          </button>
          <button
            onClick={scoreAll}
            disabled={scoring}
            className="px-3 py-2 rounded-xl border border-background-200 text-xs font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-magic-line" /> {t('sourcing.scoreAll')}
          </button>
          <button
            onClick={() => id && sourcingApi.exportCsv(id).catch((e) => setError(e.message))}
            className="px-3 py-2 rounded-xl border border-background-200 text-xs font-medium text-foreground-700 hover:bg-background-100 cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-download-2-line" /> {t('sourcing.exportCsv')}
          </button>
        </div>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onImportFile(f);
          e.target.value = '';
        }}
      />

      {notice && <p className="text-sm text-primary-600 mb-2">{notice}</p>}
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

      {/* Runs */}
      {runs.length > 0 && (
        <div className="mb-4 bg-background-50 border border-background-200 rounded-xl px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider text-foreground-400 font-medium mb-2">
            {t('sourcing.runs')}
          </p>
          <div className="space-y-1.5">
            {runs.map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-xs text-foreground-600">
                <span className="font-mono font-semibold w-5">{r.bucket || '—'}</span>
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                    r.status === 'succeeded'
                      ? 'bg-green-100 text-green-700'
                      : r.status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {t(`sourcing.runStatus.${r.status}`, r.status)}
                </span>
                <span className="text-foreground-400">
                  {r.items_imported}/{r.items_found}
                  {r.items_duplicates ? ` (+${r.items_duplicates} dup)` : ''}
                </span>
                {r.cost_usd != null && <span className="text-foreground-400">${r.cost_usd}</span>}
                <span className="text-foreground-300 truncate">{r.actor_id}</span>
                {r.status === 'succeeded' && r.apify_dataset_id && (
                  <button
                    onClick={() => importRun(r.id)}
                    className="text-primary-600 hover:underline cursor-pointer"
                  >
                    {t('sourcing.importRun')}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <select value={grade} onChange={(e) => { setGrade(e.target.value); setOffset(0); }} className="text-xs border border-background-200 rounded-lg px-2 py-1.5 cursor-pointer">
          <option value="">{t('sourcing.filterGrade')}: {t('sourcing.all')}</option>
          {['A', 'B', 'C'].map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={bucket} onChange={(e) => { setBucket(e.target.value); setOffset(0); }} className="text-xs border border-background-200 rounded-lg px-2 py-1.5 cursor-pointer">
          <option value="">{t('sourcing.filterBucket')}: {t('sourcing.all')}</option>
          {['A', 'B', 'C', 'D', 'E'].map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="text-xs border border-background-200 rounded-lg px-2 py-1.5 cursor-pointer">
          <option value="">{t('sourcing.filterStatus')}: {t('sourcing.all')}</option>
          {OUTREACH_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`sourcing.status.${s}`, s)}</option>
          ))}
        </select>
        <input
          value={minScore}
          onChange={(e) => { setMinScore(e.target.value.replace(/\D/g, '')); setOffset(0); }}
          placeholder={t('sourcing.minScore')}
          className="w-24 text-xs border border-background-200 rounded-lg px-2 py-1.5"
        />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOffset(0); }}
          placeholder={t('sourcing.search')}
          className="flex-1 min-w-48 text-xs border border-background-200 rounded-lg px-3 py-1.5"
        />
        {selected.size > 0 && (
          <button
            onClick={bulkContacted}
            className="px-3 py-1.5 rounded-lg bg-accent-500 text-white text-xs font-medium hover:bg-accent-600 cursor-pointer"
          >
            {t('sourcing.markContacted', { count: selected.size })}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-background-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-foreground-400 border-b border-background-200">
              <th className="px-3 py-2.5 w-8"></th>
              <th className="px-3 py-2.5">{t('sourcing.colName')}</th>
              <th className="px-3 py-2.5">{t('sourcing.colRole')}</th>
              <th className="px-3 py-2.5 text-right">{t('sourcing.colYears')}</th>
              <th className="px-3 py-2.5">{t('sourcing.colFlags')}</th>
              <th className="px-3 py-2.5 text-center">{t('sourcing.colScore')}</th>
              <th className="px-3 py-2.5 text-center">{t('sourcing.colGrade')}</th>
              <th className="px-3 py-2.5">{t('sourcing.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {list?.items.map((c) => {
              const effGrade = c.manual_grade || c.grade;
              return (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/sourcing/${id}/candidates/${c.id}`)}
                  className="border-b border-background-100 hover:bg-background-50 cursor-pointer"
                >
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      className="cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="font-medium text-foreground-900">{c.full_name || '—'}</p>
                    <p className="text-xs text-foreground-400 line-clamp-1">{c.location_text}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-foreground-700 line-clamp-1">{c.current_title || '—'}</p>
                    <p className="text-xs text-foreground-400 line-clamp-1">{c.current_company}</p>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-foreground-600">
                    {c.years_total_est ?? '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1 flex-wrap">
                      {c.past_founder && <Flag color="purple" label={t('sourcing.flagFounder')} />}
                      {c.fast_growth && <Flag color="green" label={t('sourcing.flagFastGrowth')} />}
                      {c.speaks_russian && <Flag color="blue" label={t('sourcing.flagRussian')} />}
                      {c.open_to_work && <Flag color="amber" label={t('sourcing.flagOpenToWork')} />}
                      {c.promoted_candidate_id && (
                        <Flag color="primary" label={t('sourcing.promoted')} />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center font-mono font-semibold text-foreground-800">
                    {c.total_score ?? '—'}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {effGrade ? <GradeBadge grade={effGrade} manual={!!c.manual_grade} /> : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-foreground-600">
                    {t(`sourcing.status.${c.outreach_status}`, c.outreach_status)}
                  </td>
                </tr>
              );
            })}
            {list && list.items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm text-foreground-400">
                  {t('sourcing.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {list && list.total > limit && (
        <div className="flex items-center justify-between mt-3 text-xs text-foreground-500">
          <span>
            {offset + 1}–{Math.min(offset + limit, list.total)} / {list.total}
          </span>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="px-3 py-1.5 rounded-lg border border-background-200 disabled:opacity-40 hover:bg-background-100 cursor-pointer"
            >
              ←
            </button>
            <button
              disabled={offset + limit >= list.total}
              onClick={() => setOffset(offset + limit)}
              className="px-3 py-1.5 rounded-lg border border-background-200 disabled:opacity-40 hover:bg-background-100 cursor-pointer"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const FLAG_COLORS: Record<string, string> = {
  purple: 'bg-purple-100 text-purple-700',
  green: 'bg-green-100 text-green-700',
  blue: 'bg-blue-100 text-blue-700',
  amber: 'bg-amber-100 text-amber-700',
  primary: 'bg-primary-100 text-primary-700',
};

function Flag({ color, label }: { color: string; label: string }) {
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${FLAG_COLORS[color]}`}>
      {label}
    </span>
  );
}

export function GradeBadge({ grade, manual }: { grade: string; manual?: boolean }) {
  const colors: Record<string, string> = {
    A: 'bg-green-100 text-green-700',
    B: 'bg-amber-100 text-amber-700',
    C: 'bg-red-100 text-red-700',
  };
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${colors[grade] || 'bg-background-100 text-foreground-500'}`}
      title={manual ? 'manual' : 'AI'}
    >
      {grade}
      {manual && <i className="ri-user-line text-[10px]" />}
    </span>
  );
}
